import { z } from 'zod'
import { idSchema, isoDateTimeSchema, milliSchema, minorNonNegativeSchema, optionalText, text } from './common.js'
import { lineTotalMinor, sumMinor } from '../money.js'

/**
 * A supplier purchase invoice, recorded for the quarterly VAT return —
 * separate from a jobcard's own cost figures. A jobcard line's `costMinor`
 * is staff's own estimate of what a part cost, entered for margin tracking;
 * this is the actual tax invoice a supplier issued, entered once for VAT
 * purposes and optionally tagged with the jobcard it was bought for.
 */
export const EXPENSE_CATEGORIES = ['general', 'parts', 'paint'] as const
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number]
export const expenseCategorySchema = z.enum(EXPENSE_CATEGORIES)

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  general: 'General',
  parts: 'Parts',
  paint: 'Paint',
}

const expenseFields = {
  category: expenseCategorySchema,
  /** The invoice's own date, "YYYY-MM-DD" — what the VAT return groups by. */
  date: z.string().min(1),
  invoiceNumber: text(80),
  supplierName: text(160),
  /**
   * Null when the supplier isn't VAT-registered. A supplier with no TRN
   * cannot legally charge VAT, so `vatMinor` must be 0 whenever this is
   * null — enforced below, not left to whoever is typing the invoice in.
   */
  supplierTrn: optionalText(40),
  amountBeforeVatMinor: minorNonNegativeSchema,
  vatMinor: minorNonNegativeSchema,
  /** The jobcard this purchase was for, when there is one. */
  jobcardId: idSchema.nullable(),
  notes: optionalText(1000),
}

function requireNoVatWithoutTrn(
  value: { supplierTrn: string | null; vatMinor: number },
  ctx: z.RefinementCtx,
) {
  if (!value.supplierTrn && value.vatMinor !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['vatMinor'],
      message: 'A supplier with no TRN cannot charge VAT',
    })
  }
}

/**
 * One line of a supplier invoice's breakdown: what was bought, how many, at
 * what unit price BEFORE VAT. No per-line VAT — the VAT is whatever the
 * supplier printed for the invoice as a whole, held on the expense itself.
 */
export const expenseLineInputSchema = z.object({
  description: text(200),
  /** Quantity in milli-units (1000 = 1). */
  quantityMilli: milliSchema,
  unitPriceMinor: minorNonNegativeSchema,
})
export type ExpenseLineInput = z.infer<typeof expenseLineInputSchema>

export const expenseLineSchema = expenseLineInputSchema.extend({ id: idSchema })
export type ExpenseLine = z.infer<typeof expenseLineSchema>

/** What a line comes to: quantity x unit price. */
export function expenseLineTotalMinor(line: Pick<ExpenseLineInput, 'quantityMilli' | 'unitPriceMinor'>): number {
  return lineTotalMinor(line.quantityMilli, line.unitPriceMinor)
}

/** Sum of the lines' totals — what the invoice's before-VAT figure has to equal when it is itemised. */
export function expenseLinesSubtotalMinor(lines: readonly Pick<ExpenseLineInput, 'quantityMilli' | 'unitPriceMinor'>[]): number {
  return sumMinor(lines.map(expenseLineTotalMinor))
}

function requireLinesMatchSubtotal(
  value: { amountBeforeVatMinor: number; lines?: ExpenseLineInput[] },
  ctx: z.RefinementCtx,
) {
  if (value.lines && value.lines.length > 0 && expenseLinesSubtotalMinor(value.lines) !== value.amountBeforeVatMinor) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['lines'],
      message: "The lines must add up to the invoice's amount before VAT",
    })
  }
}

export const expenseInputSchema = z
  .object({
    ...expenseFields,
    /**
     * The invoice's itemised breakdown. OPTIONAL with no default, on purpose:
     * absent means "leave the existing lines alone" (so a header-only edit,
     * or the Excel sync, never wipes them); present means "replace all the
     * lines with these" and an empty array clears them.
     */
    lines: z.array(expenseLineInputSchema).max(200).optional(),
  })
  .superRefine(requireNoVatWithoutTrn)
  .superRefine(requireLinesMatchSubtotal)
export type ExpenseInput = z.infer<typeof expenseInputSchema>

export const expenseSchema = z.object({
  ...expenseFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Expense = z.infer<typeof expenseSchema>

export interface ExpenseListItem extends Expense {
  /** Denormalised for the list, so a jobcard link reads as a number, not an id. */
  jobcardNumber: string | null
}

/** One expense with its itemised breakdown (empty when the invoice was never itemised). */
export interface ExpenseDetail extends ExpenseListItem {
  lines: ExpenseLine[]
}

/** Sum for one category within one month. */
export interface ExpenseCategoryTotals {
  beforeVatMinor: number
  vatMinor: number
  totalMinor: number
}

export interface ExpenseMonthSummary {
  /** "YYYY-MM" */
  month: string
  categories: Record<ExpenseCategory, ExpenseCategoryTotals>
  beforeVatMinor: number
  vatMinor: number
  totalMinor: number
}

/** The figures for one VAT return: usually 3 consecutive months. */
export interface VatReturnSummary {
  months: ExpenseMonthSummary[]
  beforeVatMinor: number
  vatMinor: number
  totalMinor: number
}

/** One row of the source sheet that couldn't be imported, and why. */
export interface ExpenseSyncRowError {
  /** 1-based, matching the row number a spreadsheet program shows. */
  row: number
  message: string
}

/**
 * What happened when the "Sync from Excel" button ran for one category.
 * Scoped to the month/quarter being viewed on the Accounting tab: a sheet
 * that tracks more than one month in the same tab (rather than a fresh tab
 * per month) can carry rows the sync intentionally leaves alone.
 */
export interface ExpenseSyncResult {
  added: number
  /** Already present (matched by category + invoice number + supplier) — left untouched. */
  skipped: number
  /** Valid rows whose date fell outside the requested range — not imported. */
  outsideRange: number
  errors: ExpenseSyncRowError[]
}
